import React from "react";
import { isCapabilityEnabled } from "owa-capabilities";
import { addinsDrawerPaneUnderRibbonCapability } from "owa-capabilities-definitions/lib/addinsDrawerPaneUnderRibbonCapability";
import ProjectionContext from "owa-popout-v2/lib/context/ProjectionContext";
import { resolveVisibleDrawerMount } from "../selectors/getDrawerPaneAnchor";
import getDrawerPaneStackOrder from "../utils/getDrawerPaneStackOrder";
import type { DrawerPaneAnchorSurface } from "../store/schema/DrawerPaneAnchorState";

export interface DrawerPaneAnchor {
    mountNode: HTMLElement | undefined;
    surface: DrawerPaneAnchorSurface | undefined;
    order: number;
    orderStyle: React.CSSProperties;
    enabled: boolean;
}

/**
 * Resolves the under-ribbon drawer anchor for a pane in one place: the mount node to
 * portal into, the owning surface, and the flex `order`/style used for stacking. This
 * consolidates the resolve -> portal -> reserve-width logic shared by `App`,
 * `AddinsResizableDrawer`, and `AddinsUnderRibbonContainer` so it isn't re-derived at
 * each call site. Reads observable anchor state, so callers that need the resolved
 * `mountNode`/`surface` to be reactive must be `observer`s.
 */
export function useDrawerPaneAnchor(drawerPaneId: string | undefined, targetWindow?: Window): DrawerPaneAnchor {
    const projectionWindow = React.useContext(ProjectionContext);
    const resolvedWindow = targetWindow ?? projectionWindow ?? window;
    const enabled = isCapabilityEnabled(addinsDrawerPaneUnderRibbonCapability);
    const mount = enabled && drawerPaneId ? resolveVisibleDrawerMount(resolvedWindow, drawerPaneId) : null;
    const order = getDrawerPaneStackOrder(drawerPaneId);
    const orderStyle = React.useMemo<React.CSSProperties>(() => ({ order }), [order]);
    
    return {
        mountNode: mount?.mountNode,
        surface: mount?.surface,
        order,
        orderStyle,
        enabled,
    };
}
